import { airportMatchesCode, cityByAirport, iataOf, isMetroCode } from '@voll-intelligence/domain';
import { airportPlaceName } from './airport_place_name.js';

/**
 * A volta parte de um aeroporto DIFERENTE daquele em que a ida chegou (#4203:
 * CNF → CGH na ida, GRU → CNF na volta). O fornecedor pode parear voltas de
 * qualquer aeroporto da praça, e isso é legítimo — às vezes é o que a pessoa
 * quer, às vezes é mais barato —, então a oferta nunca é filtrada: a troca só
 * precisa ser VISTA antes da compra.
 *
 * `sameCity`: os dois aeroportos servem a mesma cidade (CGH × GRU) — o caso em
 * que a cidade embaixo do código não desempata nada.
 * `expected`: a ponta PEDIDA era um código metro ("São Paulo, todos os
 * aeroportos") e a volta parte de um aeroporto desse metro; a pessoa pediu a
 * cidade inteira, então a troca é rótulo, nunca alerta.
 */
export interface AirportSwitch {
  outboundArrival: string;
  returnDeparture: string;
  sameCity: boolean;
  expected: boolean;
}

/** `null` quando a volta parte do mesmo aeroporto da chegada da ida, ou quando
 *  falta uma das pontas (somente ida). Puro: só as tabelas de `domain/geo`. */
export function airportSwitchOf(input: {
  outboundArrival: string | null | undefined;
  returnDeparture: string | null | undefined;
  /** O destino que a pessoa pediu (aeroporto ou metro). */
  requested?: string | null;
}): AirportSwitch | null {
  const arrival = iataOf(input.outboundArrival);
  const departure = iataOf(input.returnDeparture);
  if (!arrival || !departure || arrival === departure) return null;
  const city = cityByAirport(arrival);
  return {
    outboundArrival: arrival,
    returnDeparture: departure,
    sameCity: city !== null && city === cityByAirport(departure),
    expected: isMetroCode(input.requested) && airportMatchesCode(departure, input.requested),
  };
}

/** "GRU · São Paulo — Guarulhos": o código e o nome que desempata a cidade —
 *  "São Paulo" sozinha é a MESMA para CGH e GRU. */
export function airportLabel(iata: string): string {
  const name = airportPlaceName(iata);
  return name ? `${iata} · ${name}` : iata;
}
